/**
 * Status chips for the results domain — every clinical/lifecycle state is color + glyph + text
 * (never color alone; daltonisme + médico-légal). They map the server enums onto the UI-kit Badge
 * tones; the Badge auto-injects the mandated shape glyph for clinical tones.
 */
import { Badge } from "@/ui";
import type { BadgeTone } from "@/ui";
import { useT } from "@/app";
import type { CanalStatut, CompteRenduEtat, DrapeauResultat } from "../types";

const ETAT_TONE: Record<CompteRenduEtat, BadgeTone> = {
  genere: "neutral",
  emis: "success",
  supersede: "warn",
};

const ETAT_GLYPH: Record<CompteRenduEtat, string> = {
  genere: "○",
  emis: "✓",
  supersede: "⤳",
};

/** The compte-rendu lifecycle state (genere → emis → supersede). */
export function EtatBadge({ etat }: { etat: CompteRenduEtat }) {
  const { t } = useT();
  return (
    <Badge tone={ETAT_TONE[etat]} glyph={ETAT_GLYPH[etat]}>
      {t(`results.etat.${etat}`)}
    </Badge>
  );
}

const STATUT_TONE: Record<CanalStatut, BadgeTone> = {
  en_attente: "neutral",
  retenu: "hold", // clinical "hold" tone → auto glyph ❚❚
  libere: "info",
  delivre: "success",
  echec: "danger",
};

const STATUT_GLYPH: Partial<Record<CanalStatut, string>> = {
  en_attente: "◷",
  libere: "▷",
  delivre: "✓",
  echec: "◆",
  // retenu intentionally omitted → clinical "hold" tone supplies its mandated glyph
};

/** A diffusion channel's delivery state. */
export function StatutCanalBadge({ statut }: { statut: CanalStatut }) {
  const { t } = useT();
  const glyph = STATUT_GLYPH[statut];
  return (
    <Badge tone={STATUT_TONE[statut]} {...(glyph ? { glyph } : {})}>
      {t(`results.statut.${statut}`)}
    </Badge>
  );
}

const DRAPEAU_TONE: Record<DrapeauResultat, BadgeTone> = {
  normal: "normal",
  bas: "low",
  haut: "high",
  critique: "critical",
};

/** A frozen clinical flag on a result line (null → neutral em-dash, no chip). */
export function DrapeauBadge({ drapeau }: { drapeau: DrapeauResultat | null }) {
  const { t } = useT();
  if (!drapeau) {
    return (
      <span aria-hidden style={{ color: "var(--nv-steel)" }}>
        {t("results.drapeau.aucun")}
      </span>
    );
  }
  return <Badge tone={DRAPEAU_TONE[drapeau]}>{t(`results.drapeau.${drapeau}`)}</Badge>;
}

const TYPE_GLYPH = { partiel: "◐", complet: "●" } as const;

/** The report scope (partiel/complet). */
export function TypeBadge({ type }: { type: "partiel" | "complet" }) {
  const { t } = useT();
  return (
    <Badge tone="info" glyph={TYPE_GLYPH[type]}>
      {t(`results.type.${type}`)}
    </Badge>
  );
}
